import { useMemo } from "react";
import { useGetEligibilityCriteriaQuery } from "@lending/hooks/queries/EligibilityCriteria/useGetEligibilityCriteriaQuery";
import {
  getCoApplicantProductType,
  useIsCoApplicantEnabled,
} from "@lending/hooks/useIsCoApplicantEnabled";
import { useIsPlaidLayerEnabled } from "@lending/hooks/useIsPlaidLayerEnabled";
import { useLendingApplicationSteps } from "@lending/hooks/useLendingApplicationSteps";
import { getCoApplicant } from "@lending/utils/applicantHelpers";
import { useStore } from "../../contexts/StoreContext";
import type { StoreData } from "../../contexts/StoreContext";
import type { NavigationContext, ProductType } from "@lending/navigation";
import type {
  CreditCardApplication,
  LendingApplicationApplicantDetails,
  PersonalLoanApplication,
  VehicleLoanApplication,
} from "@lending/types";

/**
 * The lending application union used across the navigation and resume paths.
 */
export type LendingApplication =
  | PersonalLoanApplication
  | CreditCardApplication
  | VehicleLoanApplication;

type Applicant = LendingApplicationApplicantDetails;

/**
 * Auto-loan-specific navigation context. This is the exclusive delta the auto
 * flow contributes on top of the shared applicant/store-derived facts; keeping
 * it separate prevents the auto adapter from silently widening its contract to
 * the full `NavigationContext`.
 */
export interface AutoLoanApplicationContext {
  hasVehicleSelected?: boolean;
  isRefinance?: boolean;
  isAutoRequestedAmountEnabled?: boolean;
}

interface ApplicationNavigationContextInputs {
  application?: LendingApplication;
  productType: ProductType;
  storeData: Pick<StoreData, "skippedPlaidPrefill">;
  isPlaidLayerEnabled: boolean;
  isCoApplicantEnabled: boolean;
  showLivingArrangementStep?: boolean;
  showEmploymentDetailsStep?: boolean;
  hasEligibilityCriteria?: boolean;
  hasMultipleCreditCardProducts?: boolean;
  autoLoanContext?: AutoLoanApplicationContext;
}

function addressesMatch(
  left: Applicant["residential_address"],
  right: Applicant["residential_address"]
) {
  return (
    left?.street_address === right?.street_address &&
    left?.street_address_2 === right?.street_address_2 &&
    left?.city === right?.city &&
    left?.region_code === right?.region_code &&
    left?.postal_code === right?.postal_code
  );
}

/**
 * Resolve the effective `skippedPlaidPrefill` value: explicit store state wins,
 * otherwise infer from the persisted applicant. When the applicant hasn't been
 * fetched yet the value stays `undefined` so callers don't poison downstream
 * derivations with a false negative.
 */
export function getSkippedPlaidPrefill(
  storeSkippedPlaidPrefill: boolean | undefined,
  prefillDataSource: Applicant["prefill_data_source"]
): boolean | undefined {
  if (storeSkippedPlaidPrefill !== undefined) {
    return storeSkippedPlaidPrefill;
  }

  if (!prefillDataSource) {
    return undefined;
  }

  return prefillDataSource === "MANUAL";
}

export function buildApplicationNavigationContext({
  application,
  productType,
  storeData,
  isPlaidLayerEnabled,
  isCoApplicantEnabled,
  showLivingArrangementStep,
  showEmploymentDetailsStep,
  hasEligibilityCriteria,
  hasMultipleCreditCardProducts,
  autoLoanContext: autoLoanContext,
}: ApplicationNavigationContextInputs): NavigationContext {
  const primaryApplicant = application?.primary_applicant;
  const coApplicant = getCoApplicant(application);

  return {
    applicationState: application?.state,
    accountApplicationUuid: application?.account_application_uuid,
    isPlaidLayerEnabled,
    skippedPlaidPrefill: getSkippedPlaidPrefill(
      storeData.skippedPlaidPrefill,
      primaryApplicant?.prefill_data_source
    ),
    isCoApplicantEnabled,
    showLivingArrangementStep,
    showEmploymentDetailsStep,
    hasCoApplicant: Boolean(coApplicant),
    hasSeparateCoApplicantAddress: Boolean(
      coApplicant?.residential_address &&
        primaryApplicant?.residential_address &&
        !addressesMatch(primaryApplicant.residential_address, coApplicant.residential_address)
    ),
    hasMultipleCreditCardProducts,
    hasSelectedCreditCardProduct: productType === "credit-card" && Boolean(application?.product),
    hasEligibilityCriteria,
    incomeType: primaryApplicant?.income?.income_type,
    coApplicantIncomeType: coApplicant?.income?.income_type,
    ...autoLoanContext,
  };
}

export function getSelectedCreditCardProduct(
  application: LendingApplication | undefined,
  productType: ProductType
): string | undefined {
  if (productType !== "credit-card" || !application?.product) {
    return undefined;
  }

  const product = application.product as unknown;
  if (typeof product === "string") {
    return product;
  }

  if (
    typeof product === "object" &&
    product !== null &&
    "id" in product &&
    typeof product.id === "string"
  ) {
    return product.id;
  }

  return undefined;
}

/**
 * Hook wrapper around `buildApplicationNavigationContext` that reads the
 * cross-cutting inputs (institution flags, store state, eligibility) at the
 * render site. Currently invoked only on the cold portal-resume path in
 * `ApplicationLayout` and `AutoLoanApplicationLayout`; ordinary route
 * enforcement continues to use the smaller policy contexts.
 */
export function useApplicationNavigationContext(
  application: LendingApplication | undefined,
  productType: ProductType,
  autoLoanContext?: AutoLoanApplicationContext,
  hasMultipleCreditCardProducts?: boolean
): NavigationContext {
  const isPlaidLayerEnabled = useIsPlaidLayerEnabled();
  const isCoApplicantEnabled = useIsCoApplicantEnabled(getCoApplicantProductType(productType));
  const { showLivingArrangementStep, showEmploymentDetailsStep } = useLendingApplicationSteps();
  const { hasEligibilityCriteria } = useGetEligibilityCriteriaQuery();
  const { data: storeData } = useStore();
  const storeSkippedPlaidPrefill = storeData.skippedPlaidPrefill;

  return useMemo(
    () =>
      buildApplicationNavigationContext({
        application,
        productType,
        storeData: { skippedPlaidPrefill: storeSkippedPlaidPrefill },
        isPlaidLayerEnabled,
        isCoApplicantEnabled,
        showLivingArrangementStep,
        showEmploymentDetailsStep,
        hasEligibilityCriteria,
        hasMultipleCreditCardProducts,
        autoLoanContext: autoLoanContext,
      }),
    [
      application,
      autoLoanContext,
      hasEligibilityCriteria,
      hasMultipleCreditCardProducts,
      isCoApplicantEnabled,
      isPlaidLayerEnabled,
      productType,
      showEmploymentDetailsStep,
      showLivingArrangementStep,
      storeSkippedPlaidPrefill,
    ]
  );
}
